import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Check, Camera, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import LanguageSwitcher from '@/components/LanguageSwitcher';

export default function Pricing() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cycle, setCycle] = useState('monthly');
  const [checkingOut, setCheckingOut] = useState(null);

  useEffect(() => {
    api.get('/subscription-plans/public')
      .then((r) => setPlans(r.data || []))
      .catch(() => toast.error(t('pricing.loadError')))
      .finally(() => setLoading(false));
  }, [t]);

  const onSelect = async (plan) => {
    if (plan.id === 'free_trial') {
      navigate(user ? '/dashboard' : '/register');
      return;
    }
    if (!user) {
      navigate('/register');
      return;
    }
    setCheckingOut(plan.id);
    try {
      const r = await api.post('/billing/create-checkout', {
        plan_id: plan.id, billing_cycle: cycle,
      });
      window.location.href = r.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || t('auth.errors.generic'));
      setCheckingOut(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      <nav className="px-6 py-4 flex items-center justify-between max-w-7xl mx-auto">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
            <Camera className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-bold text-slate-900">PhotoPlan</span>
        </Link>
        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          {user ? (
            <Link to="/dashboard">
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                Dashboard
              </Button>
            </Link>
          ) : (
            <Link to="/login">
              <Button size="sm" variant="ghost">{t('auth.signIn')}</Button>
            </Link>
          )}
        </div>
      </nav>

      <section className="px-6 py-12 max-w-5xl mx-auto text-center">
        <h1 className="text-4xl font-bold text-slate-900 mb-3">{t('pricing.title')}</h1>
        <p className="text-slate-600 mb-8">{t('pricing.subtitle')}</p>

        <div className="inline-flex p-1 bg-white rounded-full border border-slate-200 mb-10">
          <button
            onClick={() => setCycle('monthly')}
            className={`px-4 py-1.5 text-sm rounded-full transition ${cycle === 'monthly' ? 'bg-emerald-600 text-white' : 'text-slate-700'}`}
          >
            {t('pricing.monthly')}
          </button>
          <button
            onClick={() => setCycle('yearly')}
            className={`px-4 py-1.5 text-sm rounded-full transition ${cycle === 'yearly' ? 'bg-emerald-600 text-white' : 'text-slate-700'}`}
          >
            {t('pricing.yearly')} <span className="text-xs opacity-80">· {t('pricing.yearlySavings')}</span>
          </button>
        </div>

        {loading ? (
          <div className="text-slate-500">{t('common.loading')}</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {plans.map((plan) => {
              const isCurrent = user?.plan_id === plan.id;
              const price = cycle === 'monthly' ? plan.monthly_price : plan.yearly_price;
              const isFree = plan.id === 'free_trial';
              const popular = plan.id === 'pro';
              return (
                <Card
                  key={plan.id}
                  className={`p-6 text-left ${popular ? 'border-2 border-emerald-500 shadow-lg' : ''}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-xl font-bold text-slate-900">{plan.name}</h3>
                      <p className="text-sm text-slate-600 mt-1">{plan.description}</p>
                    </div>
                    {popular && (
                      <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full font-medium">
                        {t('pricing.popularBadge')}
                      </span>
                    )}
                    {isFree && (
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">
                        {t('pricing.freeTrialBadge')}
                      </span>
                    )}
                  </div>

                  <div className="my-6">
                    {isFree ? (
                      <div className="text-3xl font-bold text-slate-900">
                        {t('pricing.trialDays', { count: plan.trial_days })}
                      </div>
                    ) : (
                      <div>
                        <span className="text-4xl font-bold text-slate-900">${price}</span>
                        <span className="text-slate-500 ml-1">
                          {cycle === 'monthly' ? t('pricing.perMonth') : t('pricing.perYear')}
                        </span>
                      </div>
                    )}
                  </div>

                  <ul className="space-y-2 mb-6 text-sm">
                    {isFree && plan.trial_credits > 0 && (
                      <li className="flex items-center gap-2 text-slate-700">
                        <Check className="w-4 h-4 text-emerald-600" />
                        {t('pricing.trialCredits', { count: plan.trial_credits })}
                      </li>
                    )}
                    {!isFree && plan.monthly_credits > 0 && (
                      <li className="flex items-center gap-2 text-slate-700">
                        <Check className="w-4 h-4 text-emerald-600" />
                        {t('pricing.credits', { count: plan.monthly_credits })}
                      </li>
                    )}
                    {(plan.features || []).map((f) => (
                      <li key={f} className="flex items-center gap-2 text-slate-700">
                        <Check className="w-4 h-4 text-emerald-600" />
                        {f}
                      </li>
                    ))}
                  </ul>

                  <Button
                    className="w-full"
                    variant={popular ? 'default' : 'outline'}
                    disabled={isCurrent || checkingOut === plan.id}
                    onClick={() => onSelect(plan)}
                  >
                    {isCurrent
                      ? t('pricing.currentPlan')
                      : (user ? t('pricing.selectPlanLoggedIn') : t('pricing.selectPlan'))}
                    {!isCurrent && <ArrowRight className="w-4 h-4 ml-2" />}
                  </Button>
                </Card>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
